import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Download,
  Build,
  MenuBook,
  Description,
  ArrowBack
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Toolkit items configuration
const toolkitItems = [
  {
    id: 'reader-driver',
    name: 'Driver Reader',
    description: 'Driver tool for RFID reader connected via USB port to desktop.',
    downloadUrl: '/api/toolkit/download/ReaderDriver.zip',
    filename: 'ReaderDriver.zip'
  },
  {
    id: 'desktop-hid-tool',
    name: 'Desktop HID Tool',
    description: 'Tool for reading RFID tags for POS machines and reader boxes.',
    downloadUrl: '/api/toolkit/download/HID_DesktopTool.zip',
    filename: 'HID_DesktopTool.zip'
  },
  {
    id: 'android-handheld-toolkit',
    name: 'Android Handheld Toolkit',
    description: 'Toolkit for handheld devices. Download APK and install on corresponding handheld device.',
    downloadUrl: '/api/toolkit/download/handheld_toolkit.apk',
    filename: 'handheld_toolkit.apk'
  }
];

// Available guides list
const guideList = [
  {
    id: 'install-reader',
    name: 'Reader Installation Guide',
    file: 'install-reader-guide.md',
    icon: '📖',
    description: 'Step-by-step guide for installing and configuring RFID readers'
  },
  {
    id: 'handheld-setup',
    name: 'Handheld Setup Guide',
    file: 'handheld-setup-guide.md',
    icon: '📱',
    description: 'Complete guide for setting up handheld devices and configuring scanning parameters'
  },
  {
    id: 'device-registration',
    name: 'Device Registration Guide',
    file: 'device-registration-guide.md',
    icon: '🔌',
    description: 'Learn how to register new handheld devices and readers in the BridgeX system'
  },
  {
    id: 'permissions',
    name: 'Permissions Guide',
    file: 'permissions-guide.md',
    icon: '🔐',
    description: 'Understanding the permission system and access controls in BridgeX'
  },
  {
    id: 'inventory-import',
    name: 'Inventory Import Guide',
    file: 'inventory-import-guide.md',
    icon: '📦',
    description: 'Complete user guide for creating and managing inventory import orders'
  },
  {
    id: 'inventory-transfer',
    name: 'Inventory Transfer Guide',
    file: 'inventory-transfer-guide.md',
    icon: '🚚',
    description: 'Step-by-step guide for transferring inventory between warehouse locations'
  }
];

const Documents = () => {
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideContent, setGuideContent] = useState('');
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [guideError, setGuideError] = useState(null);

  const loadGuide = async (guide) => {
    setLoadingGuide(true);
    setGuideError(null);
    setSelectedGuide(guide);
    try {
      const response = await fetch(`/documents/${guide.file}`);
      if (!response.ok) {
        throw new Error(`Failed to load guide: ${response.statusText}`);
      }
      const text = await response.text();
      setGuideContent(text);
    } catch (err) {
      setGuideError(err.message);
      setGuideContent('');
    } finally {
      setLoadingGuide(false);
    }
  };

  const handleBackToList = () => {
    setSelectedGuide(null);
    setGuideContent('');
    setGuideError(null);
  };

  const handleDownload = async (toolkitItem) => {
    setDownloading(toolkitItem.id);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Get API base URL from environment or use default
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const downloadUrl = `${API_BASE_URL}${toolkitItem.downloadUrl}`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = toolkitItem.filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Toolkit Section */}
      <Card sx={{ borderRadius: 2, border: '1px solid #e5e7eb', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Build sx={{ color: '#8b5cf6', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Toolkit
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {toolkitItems.map((item) => (
              <Grid item xs={12} key={item.id}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                    '&:hover': {
                      boxShadow: 2,
                      borderColor: '#8b5cf6'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => handleDownload(item)}
                        disabled={downloading === item.id}
                        sx={{
                          backgroundColor: '#8b5cf6',
                          '&:hover': {
                            backgroundColor: '#7c3aed'
                          },
                          minWidth: 140
                        }}
                      >
                        {downloading === item.id ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                            Downloading...
                          </>
                        ) : (
                          'Download'
                        )}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Documentation Section */}
      <Card sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <MenuBook sx={{ color: '#3b82f6', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Documentation
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {!selectedGuide ? (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuBook sx={{ color: '#3b82f6' }} />
                Available Guides
              </Typography>
              <List>
                {guideList.map((guide, index) => (
                  <React.Fragment key={guide.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => loadGuide(guide)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: '#f8fafc',
                            transform: 'translateX(4px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <ListItemIcon>
                          <Typography variant="h6">{guide.icon}</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={guide.name}
                          secondary={guide.description}
                          primaryTypographyProps={{
                            fontWeight: 500,
                            color: '#1e293b'
                          }}
                          secondaryTypographyProps={{
                            color: '#64748b',
                            fontSize: '0.875rem'
                          }}
                        />
                        <Description sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </ListItemButton>
                    </ListItem>
                    {index < guideList.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    variant="outlined"
                    size="small"
                  >
                    Back to Guides
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{selectedGuide.icon}</span>
                    {selectedGuide.name}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {loadingGuide ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress />
                </Box>
              ) : guideError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {guideError}
                </Alert>
              ) : (
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                    '& h1': {
                      fontSize: '2rem',
                      fontWeight: 700,
                      color: '#1e293b',
                      mb: 2,
                      mt: 0
                    },
                    '& h2': {
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      color: '#1e293b',
                      mb: 1.5,
                      mt: 3
                    },
                    '& h3': {
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#334155',
                      mb: 1,
                      mt: 2
                    },
                    '& p': {
                      color: '#475569',
                      lineHeight: 1.7,
                      mb: 2
                    },
                    '& ul, & ol': {
                      color: '#475569',
                      lineHeight: 1.8,
                      mb: 2,
                      pl: 3
                    },
                    '& li': {
                      mb: 0.5
                    },
                    '& code': {
                      backgroundColor: '#f1f5f9',
                      padding: '2px 6px',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: '#1e293b'
                    },
                    '& pre': {
                      backgroundColor: '#1e293b',
                      color: '#f1f5f9',
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      mb: 2
                    },
                    '& pre code': {
                      backgroundColor: 'transparent',
                      padding: 0,
                      color: '#f1f5f9'
                    },
                    '& blockquote': {
                      borderLeft: '4px solid #3b82f6',
                      pl: 2,
                      ml: 0,
                      color: '#64748b',
                      fontStyle: 'italic',
                      mb: 2
                    },
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      mb: 2
                    },
                    '& th, & td': {
                      border: '1px solid #e5e7eb',
                      padding: '12px',
                      textAlign: 'left'
                    },
                    '& th': {
                      backgroundColor: '#f8fafc',
                      fontWeight: 600,
                      color: '#1e293b'
                    },
                    '& a': {
                      color: '#3b82f6',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      border: '1px solid #e5e7eb',
                      mb: 2,
                      mt: 2
                    }
                  }}
                >
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      img: ({ node, ...props }) => (
                        <img {...props} style={{ maxWidth: '100%', height: 'auto' }} />
                      )
                    }}
                  >
                    {guideContent}
                  </ReactMarkdown>
                </Paper>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Documents;

